import { useCallback, useMemo, useState } from 'react';
import { PageHeader } from 'admin/shared-components/PageHeader/PageHeader';
import { useAppDispatch, useAppSelector } from 'redux-stores';
import CollapsibleTable, {
  formatTableData,
  Header,
} from 'admin/shared-components/Table/CollapsibleTable';
import mobxInjectSelect from 'common/utils/mobxInjectSelect';
import { locationsApi, useGetLocationsQuery } from 'redux-stores/admin/locationsApi';
import { type LocationT } from 'js-sdk/src/types/Locations';
import { Toast } from 'admin/shared-components/Toast/Toast';
import { useRTKQueryPagination } from 'common/hooks/useRTKQueryPagination';

const headers: Header[] = [
  {
    label: 'Location',
    key: 'name',
    type: 'cell',
  },
  {
    label: 'Personas',
    key: 'personas',
    type: 'cell',
  },
  {
    label: '',
    key: 'actions',
    hide: true,
    type: 'header',
  },
];

type MobxProps = {
  openModal: (name: string, options: unknown) => void;
};

const Locations = ({ openModal }: MobxProps) => {
  const dispatch = useAppDispatch();
  const { selectedOrganization } = useAppSelector((state) => ({
    selectedOrganization: state.admin.selectedOrganization,
  }));

  const [toasts, setToasts] = useState<{
    toastErrorOpen: boolean;
    toastSuccessOpen: boolean;
    toastErrorMessage: string;
    toastSuccessMessage: string;
  }>({
    toastErrorOpen: false,
    toastSuccessOpen: false,
    toastErrorMessage: '',
    toastSuccessMessage: '',
  });

  const {
    data: locations,
    isFetching,
    error,
    loadNextPage,
    hasNextPage,
    refetch,
    loadFirstPage,
  } = useRTKQueryPagination<LocationT>(useGetLocationsQuery, {
    orgId: selectedOrganization.token,
    count: 5,
    startIndex: 1,
  });

  const onSuccessToast = useCallback(
    (message: string) => {
      setToasts({
        ...toasts,
        toastSuccessOpen: true,
        toastSuccessMessage: message,
      });
    },
    [toasts]
  );
  const onErrorToast = useCallback(
    (message: string) => {
      setToasts({
        ...toasts,
        toastErrorOpen: true,
        toastErrorMessage: message,
      });
    },
    [toasts]
  );

  const formattedData = useMemo(
    () =>
      formatTableData(locations || [], headers, {
        childrenAreCollapsible: true,
        transformArraysFunction: (value: unknown, key: string) => {
          if (key === 'personas' && ((Array.isArray(value) && !value.length) || !value)) {
            return '-';
          }
          if (Array.isArray(value)) {
            const formattedValue = value
              .slice(0, 3)
              .map((item) => item.name)
              .join(', ');
            return value.length > 4 ? `${formattedValue}...` : formattedValue;
          }
          return value as string;
        },
        addActions: true,
        actions: [
          {
            label: 'Manage Personas',
            onClick: (data) => {
              openModal('manageLocations', {
                location: (data as { source: { extraData: LocationT } }).source.extraData,
                organizationId: selectedOrganization.token,
                resetData: () => {
                  dispatch(locationsApi.util.resetApiState());
                  loadFirstPage();
                },
                onSuccessToast,
                onErrorToast,
              });
            },
          },
        ],
      }),
    [
      dispatch,
      loadFirstPage,
      locations,
      onErrorToast,
      onSuccessToast,
      openModal,
      selectedOrganization.token,
    ]
  );

  if (isFetching && !locations?.length) {
    return (
      <div>
        <PageHeader label={selectedOrganization.name} />
        <div>Loading...</div>
      </div>
    );
  }
  return (
    <div>
      <PageHeader label={selectedOrganization.name} />
      <CollapsibleTable
        state={error ? 'error' : 'success'}
        paginated={true}
        paginationLoading={isFetching}
        rows={formattedData}
        headers={headers}
        options={{ hideInnerHeaders: true, collapsed: false }}
        loadNextPage={() => loadNextPage()}
        hasNextPage={hasNextPage}
        refetch={() => {
          refetch();
        }}
      />
      <Toast
        message={toasts.toastErrorMessage}
        type={'FAILURE'}
        open={toasts.toastErrorOpen}
        onClose={() => {
          setToasts({ ...toasts, toastErrorOpen: false });
        }}
        autoHideDuration={2000}
      />
      <Toast
        type="SUCCESS"
        message={toasts.toastSuccessMessage}
        open={toasts.toastSuccessOpen}
        onClose={() => {
          setToasts({ ...toasts, toastSuccessOpen: false });
        }}
        autoHideDuration={2000}
      />
    </div>
  );
};

export default mobxInjectSelect<{}, MobxProps>({
  modalStore: ['openModal'],
})(Locations);
